import React, { useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import * as styles from '../SideBySideVideo.module.scss'

import { BaseVideoConfig, CampaignConfig } from '../config'
import { TrackFn } from '../SideBySideVideoPage'
import StepHeader from './StepHeader'
import VideoThumb from './VideoThumb'
import { PlayIcon, PauseIcon } from 'lucide-react'

type Props = {
  config: CampaignConfig
  initialSelectedId?: string | null
  onBack: () => void
  onNext: (video: BaseVideoConfig) => void
  onEvent: TrackFn
}

const PREVIEW_VOLUME = 0.3

const VideoSelectionStage = ({
  config,
  initialSelectedId,
  onBack,
  onNext,
  onEvent,
}: Props) => {
  const { copy, baseVideos } = config
  const { t } = useTranslation('sideBySideVideo')
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSelectedId ?? baseVideos[0]?.id ?? null,
  )
  // id of the card whose preview is currently playing (only one at a time)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // measured intrinsic aspect (w/h) per video; the grid uses ONE shared
  // aspect (the median of the measured videos) so every card has the same
  // height — minority-orientation videos are center-cropped in the thumb
  // (object-fit cover), the full video still plays in its true ratio
  const [aspects, setAspects] = useState<Record<string, number>>({})
  const setAspect = (videoId: string, ratio: number) =>
    setAspects(prev =>
      prev[videoId] === ratio ? prev : { ...prev, [videoId]: ratio },
    )
  const measured = Object.values(aspects).sort((a, b) => a - b)
  const gridAspect = measured.length
    ? measured[Math.floor(measured.length / 2)]
    : undefined
  // Landscape videos get too short in a 2-up grid on phones; once measured,
  // switch those to a single full-width column on small screens.
  const isLandscapeGrid = !!gridAspect && gridAspect > 1.2

  const selected = baseVideos.find(v => v.id === selectedId) ?? null

  const selectVideo = (v: BaseVideoConfig) => {
    onEvent('step_interaction_sbsv_video_select', {
      result_id: v.id,
      result: v.title,
    })
    setSelectedId(v.id)
  }

  const handleNext = () => {
    if (!selected) return
    onEvent('step_interaction_sbsv_video_selected', {
      result_id: selected.id,
      result: selected.title,
    })
    onNext(selected)
  }

  const togglePlay = (id: string) => {
    setPlayingId(prev => (prev === id ? null : id))
  }

  return (
    <>
      <StepHeader step={1} total={4} onBack={onBack} />

      <div style={{ width: '100%' }}>
        <h2 className={styles.title}>{copy.selectionTitle}</h2>
        <p className={styles.subtitle}>{copy.selectionSubtitle}</p>
      </div>

      <div
        className={[
          styles.videoGrid,
          baseVideos.length >= 5 ? styles.videoGridMany : '',
          isLandscapeGrid ? styles.videoGridLandscape : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {baseVideos.map(v => {
          const isPlaying = playingId === v.id
          return (
            <div
              key={v.id}
              role="button"
              tabIndex={0}
              className={[
                styles.videoCard,
                v.id === selectedId ? styles.isSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => selectVideo(v)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  selectVideo(v)
                }
              }}
            >
              <div
                className={styles.videoCardThumb}
                // padding-top ratio box (height = width / aspect). Reliable on
                // iOS 15 where aspect-ratio on a flex item is ignored.
                style={
                  gridAspect
                    ? { paddingTop: `${100 / gridAspect}%` }
                    : undefined
                }
              >
                {isPlaying ? (
                  <video
                    ref={videoRef}
                    src={v.src}
                    crossOrigin="anonymous"
                    autoPlay
                    playsInline
                    onLoadedMetadata={e => {
                      e.currentTarget.volume = PREVIEW_VOLUME
                      const el = e.currentTarget
                      if (el.videoWidth && el.videoHeight) {
                        setAspect(v.id, el.videoWidth / el.videoHeight)
                      }
                    }}
                    onEnded={() => setPlayingId(null)}
                  />
                ) : (
                  <VideoThumb
                    src={v.src}
                    posterSrc={v.thumbnailSrc}
                    onAspect={ratio => setAspect(v.id, ratio)}
                  />
                )}
                <span className={styles.videoCardThumbOverlay} />
                <button
                  type="button"
                  className={styles.videoCardThumbPlay}
                  aria-label={
                    isPlaying ? t('media.pausePreview') : t('media.playPreview')
                  }
                  onClick={e => {
                    e.stopPropagation()
                    togglePlay(v.id)
                  }}
                >
                  {isPlaying ? (
                    <PauseIcon className={styles.videoCardThumbPlayIcon} />
                  ) : (
                    <PlayIcon className={styles.videoCardThumbPlayIcon} />
                  )}
                </button>
              </div>
              <p className={styles.videoCardTitle}>{v.title}</p>
              {v.description && (
                <p className={styles.videoCardDesc}>{v.description}</p>
              )}
            </div>
          )
        })}
      </div>

      <button
        type="button"
        className={styles.btn}
        disabled={!selected}
        onClick={handleNext}
        style={{ marginTop: 16 }}
      >
        {copy.selectionCta}
      </button>
    </>
  )
}

export default VideoSelectionStage
